import {
  formatCampaignDateRange,
  getCampaignLifecycleStatus,
  type CampaignLifecycleStatus,
} from "@/layouts/AppLayout/campaignLifecycle";
import type { Advertiser } from "@/types/advertiser";
import type { Agency } from "@/types/agency";
import type { CampaignSummary } from "@/types/campaign";
import { TEXT } from "@/constants/text";

export interface CampaignOverviewHeaderProps {
  title: string;
  status?: CampaignLifecycleStatus;
  subtitleParts: string[];
}

// Header content differs by selection size: a single campaign gets its own
// name, lifecycle badge, agency, and date range; multiple campaigns collapse
// to a count-based title with no badge or date range (there's no single
// campaign's dates to show).
export const deriveCampaignOverviewHeaderProps = (
  selectedCampaigns: CampaignSummary[],
  advertisers: Advertiser[],
  agencies: Agency[],
  now: Date
): CampaignOverviewHeaderProps => {
  const isSingleCampaign = selectedCampaigns.length === 1;

  if (!isSingleCampaign) {
    return {
      title: `${selectedCampaigns.length} ${TEXT.campaignOverview.multipleCampaignsTitle}`,
      status: undefined,
      subtitleParts: [],
    };
  }

  const [singleCampaign] = selectedCampaigns;
  const advertiser = advertisers.find(
    (item) => item.id === singleCampaign.advertiser_id
  );
  const agency = advertiser
    ? agencies.find((item) => item.id === advertiser.agency_id)
    : undefined;
  const dateRangeLabel = formatCampaignDateRange(
    singleCampaign.duration,
    singleCampaign.zone_name
  );

  return {
    title: singleCampaign.name,
    status: getCampaignLifecycleStatus(singleCampaign.duration, now),
    subtitleParts: [agency?.name, dateRangeLabel].filter(
      (part): part is string => Boolean(part)
    ),
  };
};
